import React, { useMemo, useState } from 'react'
import {
  createColumnHelper,
  Table,
  Link,
  Text,
  Flex,
  Button,
  Box,
} from '@workwhile/ui'
import {
  DocumentTitles,
  type DocumentType,
  documentTypes,
} from 'components/Users/I9DocumentUpload/constants'
import { format, parse } from 'date-fns'
import { useI9HubContextValue } from '../../context/I9HubProvider'
import { I9ReverificationHubFilters } from '../../components/I9HubFilters/I9ReverificationHubFilters'
import {
  getWorkAuthorizationExpiringDocument,
  toYmd,
  workAuthExpirationYmd,
} from '../../context/i9HubRows'
import { I9ReverificationDetailView } from '../I9ReverificationDetailView'

type WorkAuthorizationExpirationRow = ReturnType<
  typeof useI9HubContextValue
>['state']['reverification']['reportRows'][number]

const columnHelper = createColumnHelper<WorkAuthorizationExpirationRow>()

function formatDate(value: string | null) {
  if (!value) return '—'
  return format(parse(toYmd(value), 'yyyy-MM-dd', new Date()), 'PP')
}

function formatDocumentType(type: string | null) {
  if (!type) return '—'
  if ((documentTypes as readonly string[]).includes(type)) {
    return DocumentTitles[type as DocumentType]
  }
  return type
}

function useReverificationColumns(
  selectedUserId: number | null,
  setSelectedUserId: React.Dispatch<React.SetStateAction<number | null>>
) {
  return useMemo(
    () => [
      columnHelper.accessor('workerId', {
        header: 'Worker',
        enableSorting: false,
        cell: ({ row }) => {
          const userId = row.original.workerId
          const userName = row.original.workerName
          return (
            <Text>
              {userName ? `${userName} (` : ''}
              <Link to={`/users/${userId}`} target="_blank">
                {userId}
              </Link>
              {userName ? ')' : ''}
            </Text>
          )
        },
      }),
      columnHelper.accessor('i9WorkAuthorizationExpirationDate', {
        header: 'Work auth expires',
        id: 'workAuthExpiration',
        sortingFn: (firstRow, secondRow) =>
          workAuthExpirationYmd(firstRow.original).localeCompare(
            workAuthExpirationYmd(secondRow.original)
          ),
        cell: ({ row }) =>
          formatDate(row.original.i9WorkAuthorizationExpirationDate),
      }),
      columnHelper.display({
        id: 'documentType',
        header: 'Document Type',
        enableSorting: false,
        cell: ({ row }) => {
          const { documentType, expirationDate } =
            getWorkAuthorizationExpiringDocument(row.original)
          return (
            <Flex flexDirection="column" gap={1}>
              <Text fontSize="sm">{formatDocumentType(documentType)}</Text>
              <Text fontSize="sm" color="lightText">
                Expires: {formatDate(expirationDate)}
              </Text>
            </Flex>
          )
        },
      }),
      columnHelper.accessor('workerW2States', {
        header: 'States',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.workerW2States.length
            ? row.original.workerW2States.join(', ')
            : '—',
      }),
      columnHelper.accessor('workerMarket', {
        header: 'Market',
        enableSorting: false,
        cell: ({ row }) => row.original.workerMarket || '—',
      }),
      columnHelper.accessor('workerId', {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: (info) => {
          const workerId = info.row.original.workerId
          const isExpanded = selectedUserId === workerId
          return (
            <>
              <Flex gap={2} mb={2}>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() =>
                    setSelectedUserId(isExpanded ? null : workerId)
                  }
                >
                  Add Document
                </Button>
              </Flex>
              {isExpanded ? (
                <I9ReverificationDetailView userId={workerId} />
              ) : null}
            </>
          )
        },
      }),
    ],
    [selectedUserId, setSelectedUserId]
  )
}

type ReverificationQueueTableProps = {
  rows: WorkAuthorizationExpirationRow[]
  columns: ReturnType<typeof useReverificationColumns>
}

function ReverificationQueueTable({
  rows,
  columns,
}: ReverificationQueueTableProps) {
  return (
    <Table
      columns={columns}
      data={rows}
      textVerticalAlign="top"
      enableSorting={true}
      defaultSort={[
        {
          id: 'workAuthExpiration',
          desc: false,
        },
      ]}
      alternateRowColor={true}
    />
  )
}

export function I9ReverificationQueueMain() {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)

  const {
    state: {
      reverification: { groupedRows },
    },
  } = useI9HubContextValue()

  const columns = useReverificationColumns(selectedUserId, setSelectedUserId)

  const totalRows = groupedRows.reduce((sum, [, rows]) => sum + rows.length, 0)

  return (
    <>
      <Flex justifyContent="left" alignItems="center" mb={3}>
        <I9ReverificationHubFilters />
      </Flex>
      <Text fontWeight="bold" fontSize={3} mb={3}>
        I-9 reverification queue
      </Text>
      {totalRows === 0 ? (
        <Text>No workers in the reverification queue.</Text>
      ) : (
        groupedRows.map(([groupLabel, rows]) => (
          <Box key={groupLabel} mb={4}>
            <Text fontWeight="bold" fontSize={2} mb={2}>
              {groupLabel}
              <Text as="span" color="lightText" fontWeight="normal" ml={2}>
                ({rows.length})
              </Text>
            </Text>
            <ReverificationQueueTable rows={rows} columns={columns} />
          </Box>
        ))
      )}
    </>
  )
}
